import { createContext, useContext, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

/**
 * The one place a player's browser path is built or read.
 *
 * # The slug lives in the BROWSER, and `public_id` is still the identifier
 *
 * `/players/00000000-0000-4000-8000-000000139942` is unreadable and
 * unshareable; `/players/dad` is not. But the identifier a producer holds in
 * its own configuration, and the one every route of this API names a member by,
 * is still the UUID — a slug is editable by the commissioner, and an identifier
 * that changed when somebody tidied a name would silently redirect scores.
 *
 * So the translation happens HERE, in the client, and the API never learns
 * about it: a link is written with the slug, and the slug is turned back into a
 * UUID before anything is requested.
 *
 * # Both directions accept a UUID, and that is what keeps old links alive
 *
 * A path already written down — in a chat message, a bookmark, a producer's
 * notes — carries a UUID. `resolve` therefore takes either, and a view that
 * lands on a UUID path redirects itself to the slug one rather than 404ing.
 * `pathFor` falls back to the UUID while the roster is still loading, so a link
 * is never broken, only briefly ugly.
 *
 * The provider lives in `PlayerPathProvider.tsx`; this file holds the context
 * and the hooks, so neither module mixes a component with plain functions.
 */
export interface PlayerDirectory {
  /** The browser path for a player named by public id. */
  pathFor: (publicId: string) => string
  /**
   * The public id for a path segment, which may be a slug OR a public id.
   * Undefined means the roster has loaded and names nobody by that — a 404.
   */
  resolve: (param: string | undefined) => string | undefined
  /** The slug for a public id, or undefined while the roster is loading. */
  slugFor: (publicId: string) => string | undefined
  /** False while the roster is in flight: "not found" is not yet knowable. */
  loaded: boolean
}

export const PlayerDirectoryContext = createContext<PlayerDirectory | null>(null)

/**
 * A UUID is 36 characters with hyphens in four fixed places; a slug is
 * lowercase letters, digits and hyphens. They cannot collide, because a slug is
 * reduced to that alphabet and a bare UUID has no letters outside a-f — but the
 * check is on SHAPE rather than on trying the roster first, so a UUID for
 * somebody who has left still resolves.
 */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export function isPublicId(value: string): boolean {
  return UUID.test(value)
}

/**
 * usePlayerPaths is safe outside the provider.
 *
 * It falls back to UUID paths rather than throwing, because the alternative is
 * that one view rendered outside the layout takes the whole page down. A link
 * that reads as a UUID is ugly; a crash is not recoverable.
 */
export function usePlayerPaths(): PlayerDirectory {
  return (
    useContext(PlayerDirectoryContext) ?? {
      pathFor: (publicId: string) => `/players/${publicId}`,
      slugFor: () => undefined,
      resolve: (param: string | undefined) => param,
      loaded: true,
    }
  )
}

/**
 * usePlayerParam resolves a `:playerId` route segment to a public id, and
 * rewrites the URL to the slug form when it arrived as a UUID.
 *
 * # Why it redirects rather than simply working
 *
 * A UUID path RESOLVES — an old link must not break. But if it merely worked,
 * the ugly URL would be what the reader then copies out of the address bar and
 * shares onward, and the readable form would only ever appear to people who
 * clicked an internal link. Canonicalising on arrival means every path that is
 * shared is the readable one, wherever the reader came in from.
 *
 * `replace` rather than a push: the UUID form is not a place in the history
 * somebody should be able to go Back to, because they were never meaningfully
 * "there".
 *
 * Returns `undefined` while the roster is loading and `null` once it has loaded
 * and named nobody, which the caller renders as not found. The two are
 * different states and collapsing them shows "no such player" during the
 * request.
 */
export function usePlayerParam(param: string | undefined): string | undefined | null {
  const { resolve, slugFor, loaded } = usePlayerPaths()
  const navigate = useNavigate()
  const location = useLocation()
  const publicId = resolve(param)
  const { pathname, search } = location

  // **Every UUID segment in the path, not just this hook's own one.**
  //
  // Head-to-head names two people, and rewriting only the first left
  // `/players/dad/head-to-head/00000000-...` in the address bar — half
  // translated, which reads as a bug and is one. Walking the whole path also
  // means any future route naming two players is canonical without anybody
  // remembering to make it so.
  useEffect(() => {
    const canonical = pathname
      .split('/')
      .map((segment) => (isPublicId(segment) ? (slugFor(segment) ?? segment) : segment))
      .join('/')
    if (canonical === pathname) return
    navigate(canonical + search, { replace: true })
  }, [pathname, search, slugFor, navigate])

  if (publicId) return publicId
  return loaded ? null : undefined
}
